/* @flow */
import cx from 'classnames';
import invariant from 'invariant';
import * as React from 'react';

import BlockMemberMenuItem from 'users/components/BlockMemberMenuItem';
import Button from 'components/Button';
import Chat from 'entities/Chat';
import ChatActions from 'chat/actions/ChatActions';
import ChatAddParticipantContainer from 'chat/containers/ChatAddParticipantContainer';
import ChatMember from 'entities/ChatMember';
import ChatMembership from 'entities/ChatMembership';
import ConfirmDialog from 'components/ConfirmDialog';
import CurrentUser from 'auth/CurrentUser';
import errorLogger from 'utils/errorLogger';
import Glyph from 'components/Glyph';
import LayerTrigger from 'components/LayerTrigger';
import Link from 'components/Link';
import LoadingIndicator from 'components/LoadingIndicator';
import Menu from 'components/Menu';
import MenuButton from 'components/MenuButton';
import MenuItem from 'components/MenuItem';
import ModalMenuItem from 'components/ModalMenuItem';
import ModalTrigger from 'components/ModalTrigger';
import RelationshipModal from 'user/components/RelationshipModal';
import ReportChatSelectModal from './ReportChatSelectModal';
import StartChatButton from 'user/components/StartChatButton';
import StatusLabel from 'components/StatusLabel';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';
import UserAvatar from 'users/components/UserAvatar';
import UserLink from 'users/components/UserLink';

import './styles/ConversationSidebar.scss';

// May be omitted if the chat was loaded by v2/chats page
function hasMemberships(chat: Chat) {
  return chat.getMemberships().length > 0;
}

type Props = {
  chat: Chat,
};

class ConversationSidebarParticipants extends React.Component<Props, *> {
  state = {
    showLeftMembers: false,
    hoverOwner: false,
    loading: [],
    missingMemberships: !hasMemberships(this.props.chat),
  };

  componentDidMount() {
    if (this.state.missingMemberships) {
      // triggers a props update
      // $FlowFixMe - no type coercion for 0.93.0
      ChatActions.loadChat(this.props.chat.id);
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    const nextMissingMemberships = !hasMemberships(nextProps.chat);
    if (nextMissingMemberships) {
      this.setState({ missingMemberships: true });
      // triggers a props update
      // $FlowFixMe - no type coercion for 0.93.0
      ChatActions.loadChat(this.props.chat.id);
    } else {
      this.setState({ missingMemberships: false });
    }
  }

  getChatTypeName(): string {
    return this.props.chat.isActivityType() ? 'activity' : 'conversation';
  }

  setHoverOwnerTo(value: boolean): void {
    this.setState({ hoverOwner: value });
  }

  addMemberToChat(member: ChatMember): void {
    this.showLoadingIndicatorFor(member);

    ChatActions.addMembersToChat(this.props.chat, [member.id])
      .then(() => {
        this.hideLoadingIndicatorFor(member);
        ToastActions.showSuccessToast(
          `${member.getName()} has been added back to the ${this.getChatTypeName()}`
        );
      })
      .catch(errorLogger('ChatActions.addMembersToChat failed'));
  }

  hideLoadingIndicatorFor(member: ChatMember): void {
    const index = this.state.loading.indexOf(member.id);
    if (index > -1) {
      delete this.state.loading[index];
      this.forceUpdate();
    }
  }

  memberHasLoaded(member: ChatMember): boolean {
    return this.state.loading.indexOf(member.id) > -1;
  }

  removeMembershipFromChat(member: ChatMember): void {
    this.showLoadingIndicatorFor(member);

    ChatActions.changeMemberStatus(this.props.chat, member.id, 'removed')
      .then(() => {
        this.hideLoadingIndicatorFor(member);
        ToastActions.showSuccessToast(
          `${member.getName()} has been removed from the ${this.getChatTypeName()}`
        );
      })
      .catch(errorLogger('ChatActions.changeMemberStatus failed'));
  }

  showLoadingIndicatorFor(member: ChatMember): void {
    this.state.loading.push(member.id);
    this.forceUpdate();
  }

  toggleShowLeftMembers = () => {
    this.setState(({ showLeftMembers }) => ({ showLeftMembers: !showLeftMembers }));
  };
  renderActiveParticipants() {
    const { chat } = this.props;

    return chat
      .getOtherChatMembershipsWithState('present')
      .sort(membership => !membership.isCreator(chat))
      .map(membership => this.renderParticipant(chat, membership));
  }

  renderAddParticipants() {
    const { chat } = this.props;

    return (
      <ChatAddParticipantContainer chatUuid={chat.id} data-test="chat-add-participant-container">
        {disabled => {
          return (
            <Button
              level="text"
              className="ConversationSidebar-item-button"
              disabled={disabled}
              weight="semibold"
              eventElementName="ConversationSidebar-item-button">
              <Glyph
                name="plus-circle-fill"
                size="large"
                data-test-selector="conversation-sidebar-participant-add-people"
              />
              <span>Add people</span>
            </Button>
          );
        }}
      </ChatAddParticipantContainer>
    );
  }

  renderGroupOwner(menu: *, member: ChatMember) {
    const button = this.renderGroupOwnerButton(menu);

    return (
      <div
        key={member.id}
        className={cx('ConversationSidebar-participant', 'ConversationSidebar-participant-present')}
        onMouseLeave={() => this.setHoverOwnerTo(false)}
        onMouseEnter={() => this.setHoverOwnerTo(true)}>
        {this.renderParticipantNameTag(member)}
        {button}
      </div>
    );
  }

  renderGroupOwnerButton(menu: *) {
    let body = <StatusLabel className="ConversationSidebar-participant-label">Owner</StatusLabel>;

    if (this.state.hoverOwner) {
      body = (
        <Button
          level="secondary"
          size="xxsmall"
          className="ConversationSidebar-participant-menuButton"
          eventElementName="ConversationSidebar-participant-menuButton"
          aria-label="Toggle options">
          <Glyph size="large" name="ellipses" solo />
        </Button>
      );
    }

    return (
      <LayerTrigger layer={menu} trigger="click" placement="bottom">
        <div>{body}</div>
      </LayerTrigger>
    );
  }

  renderLeftParticipants() {
    const { chat } = this.props;
    const leftMembership = chat.getOtherChatMembershipsWithState('left');
    const removedMembership = chat.getOtherChatMembershipsWithState('removed');
    const total = leftMembership.length + removedMembership.length;

    if (total > 0) {
      let glyph;

      let leftList;
      let removedList;

      if (this.state.showLeftMembers) {
        leftList = leftMembership.map(membership => {
          return this.renderParticipant(chat, membership);
        });

        removedList = removedMembership.map(membership => {
          return this.renderParticipant(chat, membership);
        });

        glyph = <Glyph name="chevron-up" size="xsmall" />;
      } else {
        glyph = <Glyph name="chevron-down" size="xsmall" />;
      }

      const header =
        total === 1
          ? `1 person left this ${this.getChatTypeName()}`
          : `${total} people left this ${this.getChatTypeName()}`;

      return (
        <div className="ConversationSidebar-participantsleft">
          <div className="ConversationSidebar-participantsleft-header">
            <Link onClick={this.toggleShowLeftMembers}>
              <span>{header}</span>
              {glyph}
            </Link>
          </div>
          {leftList}
          {removedList}
        </div>
      );
    }

    return null;
  }

  renderMeParticipant() {
    const { chat } = this.props;

    if (chat.isIndividualType()) {
      return null;
    }

    let owner;
    if (chat.getCreatorId() === CurrentUser.getUserId()) {
      owner = <StatusLabel className="ConversationSidebar-tag-me">Owner</StatusLabel>;
    }

    return (
      <div className="ConversationSidebar-participant ConversationSidebar-participant-present">
        You
        {owner}
      </div>
    );
  }

  renderMenuForParticipant(chat: Chat, membership: ChatMembership) {
    const member = membership.getMember();

    const reportModal = <ReportChatSelectModal chat={chat} partner={member} />;

    let addToChat;
    if (chat.canAdd() && (membership.hasLeft() || membership.hasBeenRemoved())) {
      addToChat = (
        <MenuItem
          menuKey={member.id}
          disabled={chat.hasReachedParticipantNumberLimit()}
          onClick={() => this.addMemberToChat(member)}>
          <Glyph name="plus-circle-fill" size="medium" className="mrm" />
          <span>Add back to this {this.getChatTypeName()}</span>
        </MenuItem>
      );
    }

    let removeFromChat;
    if (membership.canRemove()) {
      removeFromChat = (
        <ModalMenuItem
          modal={this.renderRemoveFromChatConfirmModal(member)}
          className="ConversationSidebar-participant-menuItem-negative"
          data-test-selector={`conversation-sidebar-participant-menu-remove-from-chat-${
            member.id
          }`}>
          <Glyph name="close" size="medium" className="mrm" />
          <span data-test-remove-participant>Remove from this {this.getChatTypeName()}</span>
        </ModalMenuItem>
      );
    }

    let individualMessageStarter;
    if (member.isEligibleForChat()) {
      individualMessageStarter = (
        <MenuItem menuKey={member.id}>
          <StartChatButton subscriber={member}>
            <Glyph name="chat-add-bubble" size="medium" className="mrm" />
            <span>Send message</span>
          </StartChatButton>
        </MenuItem>
      );
    } // FIXME: Remove spans required for React 15 upgrade
    return (
      <Menu data-test-selector={`conversation-sidebar-participant-menu-${member.id}`}>
        <ModalMenuItem
          modal={this.renderRelationshipModal(member.id)}
          disabled={chat.isUnsubscribed()}>
          <Glyph name="avatar" size="medium" className="mrm" />
          <span>View profile</span>
        </ModalMenuItem>
        {addToChat}
        {removeFromChat}
        {individualMessageStarter}
        <BlockMemberMenuItem
          blocked={member.isBlocked()}
          memberId={member.getUserId()}
          memberName={member.getName()}
        />
        <ModalMenuItem
          modal={reportModal}
          className="ConversationSidebar-participant-menuItem-negative">
          <Glyph name="flag" size="medium" className="mrm" />
          <span>Report to Remind</span>
        </ModalMenuItem>
      </Menu>
    );
  }
  renderParticipant(chat: Chat, membership: ChatMembership) {
    const member = membership.getMember();
    const menu = this.renderMenuForParticipant(chat, membership); // if this is the owner, render the special "owner" tag.
    if (chat.getCreatorId() === member.id) {
      return this.renderGroupOwner(menu, member);
    }
    let text = (
      <Glyph
        size="large"
        name="ellipses"
        solo
        data-test-selector={`conversation-sidebar-participant-menu-button-${member.id}`}
      />
    );
    if (this.memberHasLoaded(member)) {
      text = <LoadingIndicator size="xsmall" />;
    } // to apply diff styling if you are present or not // (owner is always present).
    const presentClass = membership.isPresent()
      ? 'ConversationSidebar-participant-present'
      : 'ConversationSidebar-participant-notpresent';
    return (
      <div key={member.id} className={cx('ConversationSidebar-participant', presentClass)}>
        {this.renderParticipantNameTag(member)}
        <MenuButton
          menu={menu}
          className="ConversationSidebar-participant-menuButton"
          activeClassName="ConversationSidebar-participant-menuButton--active"
          level="icon"
          size="xsmall"
          alignment="right">
          {text}
        </MenuButton>
      </div>
    );
  }
  renderParticipantNameTag(member: ChatMember) {
    return (
      <ModalTrigger key={member.id} modal={this.renderRelationshipModal(member.id)}>
        <Text className="ConversationSidebar-participant-info" truncate>
          <UserLink className="ConversationSidebar-participant-link" userId={member.id}>
            <UserAvatar
              className="ConversationSidebar-participant-droplet"
              user={member.toJSON()}
              size="small"
              hideBadge
            />
            {member.getName()}
          </UserLink>
        </Text>
      </ModalTrigger>
    );
  }
  renderRelationshipModal(userId: Backbone$Id) {
    invariant(typeof userId === 'number', 'expected number userId');
    return <RelationshipModal userId={userId} />;
  }
  renderRemoveFromChatConfirmModal(member: ChatMember) {
    return (
      <ConfirmDialog
        eventScreenName="ConversationSidebarParticipants"
        title={`Remove ${member.getName()} from the conversation?`}
        actionButtonText="Remove"
        cancelButtonText="Cancel"
        onConfirm={() => this.removeMembershipFromChat(member)}
        subtext={`${member.getName()} won’t be able to view
          or send messages to this ${this.getChatTypeName()}.`}
      />
    );
  }
  render() {
    if (this.state.missingMemberships) {
      return (
        <LoadingIndicator size="xsmall" className="ConversationSidebar-participants-loading" />
      );
    }
    return (
      <div data-test-selector="conversation-sidebar-participants">
        {this.renderAddParticipants()}
        {this.renderMeParticipant()}
        {this.renderActiveParticipants()}
        {this.renderLeftParticipants()}
      </div>
    );
  }
}
export default ConversationSidebarParticipants;
